import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import usePagination from "../../../Components/Hooks/usePagination";
import { vendorInvoiceAPI } from "../../../helpers/api/vendorInvoice";
import { selectPhases } from "../../../slices/phases/selectors";
import { selectCostSummary } from "../../../slices/report/selectors";
import { selectCostTypes } from "../../../slices/vendorInvoiceConfig/selectors";
import { getPhases } from "../../../slices/thunks";
import { fetchCostTypesAndCategories } from "../../../slices/vendorInvoiceConfig/thunk";
import { fetchCostSummary } from "../../../slices/report/thunk";

import { buildApiParams, applyFiltersToParams, deepFiltersFromParams } from "../utils/filters";

export default function useInvoiceList() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const phases      = useSelector(selectPhases);
  const costSummary = useSelector(selectCostSummary);
  const costTypes   = useSelector(selectCostTypes);

  const [invoices,   setInvoices]   = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [filters,    setFiltersState] = useState(() => deepFiltersFromParams(searchParams));

  const filtersRef = useRef(filters);
  const pageRef    = useRef(parseInt(searchParams.get("page") || "1", 10));
  useEffect(() => { filtersRef.current = filters; }, [filters]);

  const fetchInvoices = useCallback(async (page, f) => {
    setLoading(true);
    try {
      const res = await vendorInvoiceAPI.getAll(buildApiParams(page, f));
      setInvoices(res.results ?? []);
      setPagination(res.pagination ?? null);
    } catch {
      toast.error("Failed to fetch invoices");
    } finally {
      setLoading(false);
    }
  }, []);

  const onPageChange = useCallback((page) => {
    pageRef.current = page;
    fetchInvoices(page, filtersRef.current);
    setSearchParams(applyFiltersToParams(filtersRef.current, page));
  }, [fetchInvoices, setSearchParams]);

  const { currentPage, nextPage, prevPage, resetPage } = usePagination(onPageChange, pageRef.current);

  useEffect(() => {
    fetchInvoices(pageRef.current, filtersRef.current);
    dispatch(getPhases());
    dispatch(fetchCostTypesAndCategories());
    dispatch(fetchCostSummary(filtersRef.current.phaseId || null));
  }, []);

  const setFilters = useCallback((newFilters) => {
    const phaseChanged = newFilters.phaseId !== filtersRef.current.phaseId;
    filtersRef.current = newFilters;
    setFiltersState(newFilters);
    setSearchParams(applyFiltersToParams(newFilters, 1));
    resetPage();
    if (phaseChanged) dispatch(fetchCostSummary(newFilters.phaseId || null));
  }, [resetPage, dispatch, setSearchParams]);

  const refresh = useCallback(async () => {
    await fetchInvoices(pageRef.current, filtersRef.current);
    dispatch(fetchCostSummary(filtersRef.current.phaseId || null));
  }, [fetchInvoices, dispatch]);

  return {
    invoices, pagination, loading,
    phases, costSummary, costTypes,
    filters, setFilters,
    currentPage, nextPage, prevPage,
    refresh,
  };
}
